import React, { useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, LabelList, BarChart, Bar,
} from 'recharts';
import T from '../theme/tokens';
import {
  ExamRecord, PASS_MARK, computeStats, fmtDate, groupAssessments, pooledAttempts,
} from '../utils/examAnalytics';
import { Card, FONT, InsightHero, NUM } from './AnalyticsKit';
import { EmptyState, Skeleton } from './ui';
import {
  activeDotHalo, chartAxis, chartCursorBar, chartCursorLine, chartGrid, chartXAxis,
  passLineLabel, passLineProps, seriesAnimation, tooltipWrapper,
} from '../theme/chartTheme';

interface TeacherAnalyticsPanelProps {
  records: ExamRecord[];
  examsLoading: boolean;
  attemptsProgress: { done: number; total: number } | null;
  onOpenExam?: (examId: number) => void;
}

const LINE = '#6D4AE8';
const LINE_END = '#4F46E5';
const BAR = '#6366F1';

/** "Class 9 · A3" → { cls: "9", section: "A3" } */
const splitClassLabel = (label: string) => {
  const m = label.match(/^Class\s+(\S+)(?:\s*·\s*(\S+))?/i);
  return { cls: m?.[1] ?? label, section: m?.[2] ?? '' };
};

const TeacherAnalyticsPanel: React.FC<TeacherAnalyticsPanelProps> = ({ records, examsLoading, attemptsProgress }) => {
  const loaded = useMemo(() => records.filter((r) => r.attempts && !r.pending && r.classKey !== 'unknown'), [records]);

  /* Class → sections available */
  const classMap = useMemo(() => {
    const m = new Map<string, Map<string, string>>(); // cls -> (section -> classKey)
    loaded.forEach((r) => {
      const { cls, section } = splitClassLabel(r.classLabel);
      const secs = m.get(cls) ?? new Map<string, string>();
      secs.set(section || '—', r.classKey);
      m.set(cls, secs);
    });
    return m;
  }, [loaded]);

  const classes = useMemo(() => Array.from(classMap.keys()).sort((a, b) => a.localeCompare(b, undefined, { numeric: true })), [classMap]);
  const [cls, setCls] = useState('');
  const [section, setSection] = useState('');

  const sections = useMemo(() => Array.from(classMap.get(cls)?.keys() ?? []).sort(), [classMap, cls]);

  useEffect(() => { if (classes.length && !classes.includes(cls)) setCls(classes[0]); }, [classes, cls]);
  useEffect(() => { if (sections.length && !sections.includes(section)) setSection(sections[0]); }, [sections, section]);

  const classKey = classMap.get(cls)?.get(section) ?? '';

  /* One point per assessment (PT-1, FA-1, …) in date order */
  const points = useMemo(() => {
    const recs = loaded.filter((r) => r.classKey === classKey);
    return groupAssessments(recs).map((p) => {
      const s = computeStats(pooledAttempts(p));
      return {
        label: p.label,
        date: p.date,
        avg: s.avg == null ? null : +s.avg.toFixed(1),
        n: s.n,
        passRate: s.passRate == null ? null : +s.passRate.toFixed(1),
        bands: s.bands,
        names: p.records.map((r) => r.name),
      };
    });
  }, [loaded, classKey]);

  const scoredResults = points.reduce((sum, point) => sum + point.n, 0);
  const hasAverages = points.some((point) => point.avg != null);

  const stillLoading = examsLoading || (attemptsProgress != null && attemptsProgress.done < attemptsProgress.total && loaded.length === 0);
  const scopeLabel = cls ? `Class ${cls}${section && section !== '—' ? ` · Section ${section}` : ''}` : '';

  return (
    <div style={{ display: 'grid', gap: 20, fontFamily: FONT }}>
      <InsightHero
        eyebrow="Outcome analytics"
        title="Class Performance"
        insight="Choose a class and section to see how the students' overall performance has moved across exams."
      />

      {/* Filters */}
      <Card padding="16px 20px">
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end' }}>
          <FilterSelect label="Class" value={cls} onChange={(v) => { setCls(v); setSection(''); }}
            options={classes.map((c) => ({ value: c, label: `Class ${c}` }))} disabled={stillLoading || classes.length === 0} />
          <FilterSelect label="Section" value={section} onChange={setSection}
            options={sections.map((s) => ({ value: s, label: s === '—' ? 'All' : `Section ${s}` }))} disabled={stillLoading || sections.length === 0} />
          {attemptsProgress && attemptsProgress.done < attemptsProgress.total && (
            <div role="status" style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: T.text.tertiary, fontWeight: 600, alignSelf: 'center' }}>
              <span aria-hidden style={{ width: 8, height: 8, borderRadius: 999, background: T.color.brand[500], animation: 'glow-pulse 1.6s ease-in-out infinite' }} />
              <span className="sl-num">Loading exam results {attemptsProgress.done}/{attemptsProgress.total}…</span>
            </div>
          )}
        </div>
      </Card>

      {/* Line graph */}
      <Card padding="24px 24px 16px">
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
          <div style={{ minWidth: 0 }}>
            <div className="sl-eyebrow" style={{ color: T.color.brand[600], marginBottom: 6 }}>Trend</div>
            <h2 className="sl-card__title" style={{ fontSize: 18 }}>Overall performance across exams</h2>
            <div className="sl-card__sub" style={{ fontSize: 13 }}>
              {scopeLabel ? <>{scopeLabel} — </> : null}
              class average score in each exam, in the order they were taken.
            </div>
          </div>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: T.text.secondary, fontWeight: 600 }}>
              <span aria-hidden style={{ width: 20, height: 4, borderRadius: 999, background: `linear-gradient(90deg, ${LINE}, ${LINE_END})` }} /> Class average
            </span>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: T.text.secondary, fontWeight: 600 }}>
              <span aria-hidden style={{ width: 20, borderTop: `2px dashed ${T.color.danger.solid}` }} /> Pass mark {PASS_MARK}%
            </span>
          </div>
        </div>

        {stillLoading ? (
          <div aria-busy="true" style={{ display: 'grid', gap: 12 }}>
            <Skeleton height={340} radius={14} />
            <div style={{ display: 'flex', gap: 12 }}>
              {[0, 1, 2, 3].map((i) => <Skeleton key={i} height={12} width={70} radius={6} />)}
            </div>
          </div>
        ) : points.length === 0 || !hasAverages ? (
          <EmptyState
            icon="chart"
            title="No corrected exams for this class and section yet."
            body={scopeLabel ? `Once exams for ${scopeLabel} are corrected, the class average for each exam will appear here.` : 'Pick a class and section above to see its trend.'}
          />
        ) : (
          <div
            role="img"
            aria-label={`Line chart of class average across ${points.length} exam${points.length === 1 ? '' : 's'}${scopeLabel ? ` for ${scopeLabel}` : ''}. ${points.map((p) => `${p.label}: ${p.avg == null ? 'no data' : `${p.avg}%`}`).join(', ')}.`}
            style={{ height: 380 }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={points} margin={{ top: 36, right: 24, bottom: 4, left: 4 }}>
                <defs>
                  <linearGradient id="perfLine" x1="0" x2="1" y1="0" y2="0">
                    <stop offset="0%" stopColor={LINE} />
                    <stop offset="100%" stopColor={LINE_END} />
                  </linearGradient>
                  <linearGradient id="perfArea" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor={LINE} stopOpacity={0.24} />
                    <stop offset="100%" stopColor={LINE} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid {...chartGrid} />
                <XAxis {...chartXAxis} dataKey="label" padding={{ left: 32, right: 32 }} interval="preserveStartEnd" minTickGap={12}
                  tick={{ ...chartXAxis.tick, fontSize: 12, fontWeight: 600, fill: T.text.secondary }} />
                <YAxis {...chartAxis} domain={[0, 100]} ticks={[0, 20, 40, 60, 80, 100]} tickFormatter={(v) => `${v}%`} width={52} />
                <ReferenceLine y={PASS_MARK} {...passLineProps} label={passLineLabel(`Pass ${PASS_MARK}%`)} ifOverflow="extendDomain" />
                <Tooltip {...tooltipWrapper} cursor={chartCursorLine} content={(props: any) => <PerfTooltip {...props} />} />
                <Area type="monotone" dataKey="avg" stroke="none" fill="url(#perfArea)" tooltipType="none" legendType="none" connectNulls {...seriesAnimation()} />
                <Line type="monotone" dataKey="avg" name="Class average" stroke="url(#perfLine)" strokeWidth={3} strokeLinecap="round" connectNulls
                  dot={{ r: 4.5, strokeWidth: 2.5, stroke: '#FFFFFF', fill: LINE }}
                  activeDot={activeDotHalo(LINE)} {...seriesAnimation()}>
                  {points.length <= 8 && <LabelList dataKey="avg" content={PointLabel} />}
                </Line>
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>
      {!stillLoading && scoredResults > 0 && <div className="sl-outcome-grid">
        <Card>
          <div className="sl-eyebrow" style={{ color: T.color.brand[600], marginBottom: 6 }}>Assessment outcomes</div>
          <h2 className="sl-card__title" style={{ fontSize: 18 }}>Pass rate by assessment</h2>
          <p className="sl-card__sub" style={{ margin: '4px 0 16px' }}>Percentage of scored results at or above {PASS_MARK}%. Hover for the exact rate.</p>
          <div
            role="img"
            aria-label={`Bar chart of pass rate by assessment. ${points.map((p) => `${p.label}: ${p.passRate == null ? 'no data' : `${p.passRate}%`}`).join(', ')}.`}
            style={{ height: 270, minWidth: 0 }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={points} margin={{ top: 22, right: 12, left: 4, bottom: 4 }}>
                <defs>
                  <linearGradient id="passBar" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor={BAR} stopOpacity={1} />
                    <stop offset="100%" stopColor={BAR} stopOpacity={0.55} />
                  </linearGradient>
                </defs>
                <CartesianGrid {...chartGrid} />
                <XAxis {...chartXAxis} dataKey="label" interval="preserveStartEnd" minTickGap={8} />
                <YAxis {...chartAxis} domain={[0, 100]} tickFormatter={(v) => `${v}%`} width={48} />
                <Tooltip {...tooltipWrapper} cursor={chartCursorBar} content={(props: any) => <PassTooltip {...props} />} />
                <Bar dataKey="passRate" fill="url(#passBar)" radius={[8, 8, 2, 2]} maxBarSize={36} {...seriesAnimation()}>
                  <LabelList dataKey="passRate" position="top" formatter={(v: any) => (v == null ? '' : `${v}%`)}
                    style={{ fontSize: 11, fontWeight: 700, fill: T.text.secondary, fontFamily: FONT }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>}
    </div>
  );
};

/* Value bubble above each point */
const PointLabel = (props: any) => {
  const { x, y, value } = props;
  if (value == null) return null;
  const text = `${Number(value).toFixed(1)}%`;
  const w = text.length * 7 + 14;
  return (
    <g>
      <rect x={x - w / 2} y={y - 34} width={w} height={22} rx={8} fill="#1B1F3B" />
      <path d={`M${x - 4} ${y - 12} L${x} ${y - 8} L${x + 4} ${y - 12} Z`} fill="#1B1F3B" />
      <text x={x} y={y - 19} textAnchor="middle" fontSize={11.5} fontWeight={700} fill="#FFFFFF" fontFamily={FONT} style={{ fontVariantNumeric: 'tabular-nums' }}>{text}</text>
    </g>
  );
};

const tooltipShell: React.CSSProperties = {
  background: 'rgba(255,255,255,0.96)', border: '1px solid #E3E7EF', borderRadius: 12,
  backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
  boxShadow: T.shadow.xl, padding: '12px 14px', fontFamily: FONT, minWidth: 190, maxWidth: 280,
};

const PerfTooltip: React.FC<{ active?: boolean; payload?: any[] }> = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div style={tooltipShell}>
      <div style={{ fontSize: 13.5, fontWeight: 700, color: T.text.primary }}>{p.label}</div>
      <div style={{ fontSize: 11.5, color: T.text.tertiary, fontWeight: 600, marginTop: 1 }}>{fmtDate(p.date)}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 8 }}>
        <span style={{ ...NUM, fontSize: 24, fontWeight: 800, color: LINE }}>{p.avg == null ? '—' : `${p.avg}%`}</span>
        <span style={{ fontSize: 12, color: T.text.secondary, fontWeight: 600 }}>class average</span>
      </div>
      <div className="sl-num" style={{ fontSize: 12, color: T.text.secondary, marginTop: 4 }}>{p.n} students</div>
      <div style={{ fontSize: 11, color: T.text.tertiary, marginTop: 8, paddingTop: 8, borderTop: '1px solid #EEF1F5', lineHeight: 1.5 }}>{p.names.join(', ')}</div>
    </div>
  );
};

const PassTooltip: React.FC<{ active?: boolean; payload?: any[]; label?: any }> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const value = payload[0].value;
  return (
    <div style={{ ...tooltipShell, minWidth: 150, padding: '10px 12px' }}>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: T.text.primary, marginBottom: 6 }}>{label}</div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, fontSize: 12 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: T.text.secondary, fontWeight: 600 }}>
          <span aria-hidden style={{ width: 8, height: 8, borderRadius: 2, background: BAR }} />
          Pass rate
        </span>
        <span className="sl-num" style={{ color: T.text.primary, fontWeight: 800 }}>{`${value}%`}</span>
      </div>
    </div>
  );
};

const FilterSelect: React.FC<{
  label: string; value: string; onChange: (v: string) => void; options: { value: string; label: string }[]; disabled?: boolean;
}> = ({ label, value, onChange, options, disabled }) => (
  <label style={{ display: 'grid', gap: 8 }}>
    <span className="sl-eyebrow">{label}</span>
    <select className="sl-select" value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}
      style={{ minWidth: 200, backgroundColor: disabled ? '#F8F9FC' : '#FFFFFF', cursor: disabled ? 'default' : 'pointer' }}>
      {options.length === 0 && <option value="">—</option>}
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </label>
);

export default TeacherAnalyticsPanel;
